import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { productIncentivesAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { PRIVILEGES } from '../config/roles';
import { useAuth } from '../context/AuthContext';
import {
  Award, Save, X, Package, Info, Plus, Trash2, ChevronDown, ChevronUp,
  Search, Settings2, ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';

const emptyTier = () => ({
  min_quantity: '0',
  max_quantity: '',
  incentive_per_unit: '',
});

const emptyDraft = () => ({
  use_tiers: true,
  incentive_per_unit: '',
  min_quantity: '0',
  min_per_order: '0',
  volume_unit: 'm3',
  is_active: true,
  tiers: [emptyTier()],
});

const isDraftConfigured = (d) => {
  if (!d?.is_active) return false;
  if (d.use_tiers) {
    return (d.tiers || []).some((t) => parseFloat(t.incentive_per_unit) > 0);
  }
  return parseFloat(d.incentive_per_unit) > 0;
};

const formatTierRange = (tier, unit) => {
  const min = parseFloat(tier.min_quantity) || 0;
  const max = tier.max_quantity === '' || tier.max_quantity == null
    ? null
    : parseFloat(tier.max_quantity);
  if (max == null) return `Above ${min.toLocaleString('en-IN')} ${unit}`;
  if (min <= 0) return `Up to ${max.toLocaleString('en-IN')} ${unit}`;
  return `${min.toLocaleString('en-IN')} – ${max.toLocaleString('en-IN')} ${unit}`;
};

const ProductIncentiveEditor = ({
  row,
  draft,
  canEdit,
  isExpanded,
  onToggleExpand,
  onUpdateDraft,
  onUpdateTier,
  onAddTier,
  onRemoveTier,
  onSetup,
}) => {
  const d = draft || emptyDraft();
  const configured = isDraftConfigured(d);
  const unitLabel = d.volume_unit === 'units' ? 'bricks/units' : d.volume_unit;

  return (
    <div
      className={`bg-white border rounded-2xl overflow-hidden transition-shadow ${
        configured ? 'border-emerald-200 shadow-sm shadow-emerald-100/50' : 'border-slate-200'
      }`}
    >
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-slate-50/80 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-slate-800">{row.product_name}</h3>
            {configured ? (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                Configured
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                No rules
              </span>
            )}
            {!row.product_is_active && (
              <span className="text-[10px] font-bold uppercase text-slate-400">Hidden in catalog</span>
            )}
          </div>
          {row.sub_item_label && (
            <p className="text-xs text-slate-500 mt-0.5">{row.sub_item_label}</p>
          )}
          {configured && !isExpanded && (
            <p className="text-xs text-emerald-700 mt-2">
              {(d.tiers || [])
                .filter((t) => parseFloat(t.incentive_per_unit) > 0)
                .map((t) => `${formatTierRange(t, unitLabel)} @ ${CURRENCY.SYMBOL}${t.incentive_per_unit}`)
                .join(' · ')}
            </p>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 sm:px-5 pb-5 pt-0 border-t border-slate-100 space-y-4">
              {!configured && canEdit && (
                <button
                  type="button"
                  onClick={onSetup}
                  className="w-full py-2.5 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors"
                >
                  + Start with quantity tiers
                </button>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(d.is_active)}
                    disabled={!canEdit}
                    onChange={(e) => onUpdateDraft({ is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 disabled:opacity-50"
                  />
                  Incentive active
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Quantity unit</label>
                  <select
                    value={d.volume_unit}
                    disabled={!canEdit}
                    onChange={(e) => onUpdateDraft({ volume_unit: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                  >
                    <option value="m3">m³</option>
                    <option value="units">units / bricks</option>
                    <option value="tons">tons</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Min per order line</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={!canEdit}
                    value={d.min_per_order}
                    onChange={(e) => onUpdateDraft({ min_per_order: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Skip lines below this (e.g. RMC min 8 m³)</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Rate type</label>
                  <select
                    value={d.use_tiers ? 'tiered' : 'flat'}
                    disabled={!canEdit}
                    onChange={(e) => onUpdateDraft({ use_tiers: e.target.value === 'tiered' })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                  >
                    <option value="tiered">Varying by quantity (tiers)</option>
                    <option value="flat">Single flat rate</option>
                  </select>
                </div>
              </div>

              {d.use_tiers ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-700">Quantity brackets (monthly total)</p>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={onAddTier}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add bracket
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                          <th className="text-left px-3 py-2.5">From</th>
                          <th className="text-left px-3 py-2.5">To (blank = no max)</th>
                          <th className="text-left px-3 py-2.5">Rate ({CURRENCY.SYMBOL})</th>
                          <th className="text-left px-3 py-2.5 hidden sm:table-cell">Preview</th>
                          {canEdit && <th className="w-10" />}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(d.tiers || []).map((tier, idx) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                disabled={!canEdit}
                                value={tier.min_quantity}
                                onChange={(e) => onUpdateTier(idx, 'min_quantity', e.target.value)}
                                className="w-full min-w-[5rem] px-2 py-1.5 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                disabled={!canEdit}
                                value={tier.max_quantity}
                                onChange={(e) => onUpdateTier(idx, 'max_quantity', e.target.value)}
                                placeholder="∞"
                                className="w-full min-w-[5rem] px-2 py-1.5 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                disabled={!canEdit}
                                value={tier.incentive_per_unit}
                                onChange={(e) => onUpdateTier(idx, 'incentive_per_unit', e.target.value)}
                                className="w-full min-w-[5rem] px-2 py-1.5 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                              />
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 hidden sm:table-cell">
                              {parseFloat(tier.incentive_per_unit) > 0
                                ? formatTierRange(tier, unitLabel)
                                : '—'}
                            </td>
                            {canEdit && (
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => onRemoveTier(idx)}
                                  disabled={(d.tiers || []).length <= 1}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30"
                                  title="Remove bracket"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Monthly total quantity picks one bracket; bonus = total × that bracket&apos;s rate.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Flat rate / unit</label>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">{CURRENCY.SYMBOL}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        disabled={!canEdit}
                        value={d.incentive_per_unit}
                        onChange={(e) => onUpdateDraft({ incentive_per_unit: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Min monthly qty</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={!canEdit}
                      value={d.min_quantity}
                      onChange={(e) => onUpdateDraft({ min_quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ProductIncentives = () => {
  const { hasAllPrivileges, user, loading: authLoading } = useAuth();
  const canEdit = !authLoading && (
    String(user?.role || '').toLowerCase() === 'admin'
    || hasAllPrivileges([PRIVILEGES.MANAGE_INCENTIVES])
  );

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [localSearch, setLocalSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const cardRefs = useRef({});
  const { query: globalSearch } = useGlobalSearch();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await productIncentivesAPI.getAll();
      const data = res.data?.data || [];
      setRows(data);
      const nextDraft = {};
      const nextExpanded = {};
      data.forEach((row) => {
        const hasTiers = (row.tiers || []).length > 0;
        nextDraft[row.product_id] = {
          use_tiers: row.use_tiers === 1 || hasTiers,
          incentive_per_unit:
            row.incentive_per_unit > 0 ? String(row.incentive_per_unit) : '',
          min_quantity: String(row.min_quantity ?? 0),
          min_per_order: String(row.min_per_order ?? 0),
          volume_unit: row.volume_unit || 'm3',
          is_active: row.has_config ? row.is_active === 1 : false,
          tiers: hasTiers
            ? row.tiers.map((t) => ({
              min_quantity: String(t.min_quantity ?? 0),
              max_quantity: t.max_quantity != null ? String(t.max_quantity) : '',
              incentive_per_unit: String(t.incentive_per_unit ?? ''),
            }))
            : [emptyTier()],
        };
        if (isDraftConfigured(nextDraft[row.product_id])) {
          nextExpanded[row.product_id] = true;
        }
      });
      setDraft(nextDraft);
      setExpanded(nextExpanded);
    } catch (err) {
      setError(err.message || 'Failed to load product incentives');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const d = draft[row.product_id];
      const configured = isDraftConfigured(d);
      if (filter === 'configured' && !configured) return false;
      if (filter === 'unconfigured' && configured) return false;
      const searchFields = [row.product_name, row.sub_item_label, row.volume_unit];
      if (!matchesGlobalSearch(globalSearch, searchFields)) return false;
      if (localSearch && !matchesGlobalSearch(localSearch, searchFields)) return false;
      return true;
    });
  }, [rows, draft, filter, globalSearch, localSearch]);

  const stats = useMemo(() => {
    const configured = rows.filter((r) => isDraftConfigured(draft[r.product_id])).length;
    return { total: rows.length, configured, unconfigured: rows.length - configured };
  }, [rows, draft]);

  const updateDraft = (productId, patch) => {
    setDraft((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || emptyDraft()), ...patch },
    }));
  };

  const updateTier = (productId, tierIndex, field, value) => {
    setDraft((prev) => {
      const d = prev[productId] || emptyDraft();
      const tiers = [...(d.tiers || [])];
      tiers[tierIndex] = { ...tiers[tierIndex], [field]: value };
      return { ...prev, [productId]: { ...d, tiers } };
    });
  };

  const addTier = (productId) => {
    setDraft((prev) => {
      const d = prev[productId] || emptyDraft();
      return { ...prev, [productId]: { ...d, tiers: [...(d.tiers || []), emptyTier()] } };
    });
  };

  const removeTier = (productId, tierIndex) => {
    setDraft((prev) => {
      const d = prev[productId] || emptyDraft();
      const tiers = (d.tiers || []).filter((_, i) => i !== tierIndex);
      return { ...prev, [productId]: { ...d, tiers: tiers.length ? tiers : [emptyTier()] } };
    });
  };

  const setupProduct = (productId) => {
    updateDraft(productId, {
      use_tiers: true,
      is_active: true,
      tiers: [emptyTier()],
    });
    setExpanded((prev) => ({ ...prev, [productId]: true }));
  };

  const toggleExpand = (productId) => {
    setExpanded((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const scrollToProduct = (productId) => {
    setExpanded((prev) => ({ ...prev, [productId]: true }));
    setTimeout(() => {
      cardRefs.current[productId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const items = rows.map((row) => {
        const d = draft[row.product_id] || emptyDraft();
        return {
          product_id: row.product_id,
          use_tiers: d.use_tiers,
          incentive_per_unit: parseFloat(d.incentive_per_unit) || 0,
          min_quantity: parseFloat(d.min_quantity) || 0,
          min_per_order: parseFloat(d.min_per_order) || 0,
          volume_unit: d.volume_unit || 'm3',
          is_active: d.is_active,
          tiers: (d.tiers || []).map((t) => ({
            min_quantity: parseFloat(t.min_quantity) || 0,
            max_quantity: t.max_quantity === '' ? null : parseFloat(t.max_quantity),
            incentive_per_unit: parseFloat(t.incentive_per_unit) || 0,
          })),
        };
      });
      await productIncentivesAPI.saveAll({ items });
      setSuccess('Incentive rules saved successfully');
      await load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save incentives');
    } finally {
      setSaving(false);
    }
  };

  const configuredRows = filteredRows.filter((r) => isDraftConfigured(draft[r.product_id]));
  const unconfiguredRows = filteredRows.filter((r) => !isDraftConfigured(draft[r.product_id]));

  return (
    <div className="admin-page space-y-6">
      <div className="admin-page-header">
        <div className="admin-page-header-title flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/20 flex-shrink-0">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-800">Incentive Configuration</h1>
            <p className="text-sm text-slate-500">
              Edit AAC &amp; RMC tiers, or add quantity-based incentive rules for any product.
            </p>
          </div>
        </div>
        <div className="admin-page-header-actions flex flex-wrap gap-2">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-colors"
          >
            <Package className="w-4 h-4" />
            Product Catalog
            <ExternalLink className="w-3.5 h-3.5 opacity-50" />
          </Link>
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save all rules'}
            </button>
          )}
        </div>
      </div>

      {!canEdit && !authLoading && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-sm">
          View-only access. Contact an admin to edit incentive rules.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Catalog products</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">With incentive rules</p>
          <p className="text-2xl font-bold text-emerald-800 mt-1">{stats.configured}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Not configured</p>
          <p className="text-2xl font-bold text-slate-700 mt-1">{stats.unconfigured}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
          />
        </div>
        <div className="flex rounded-xl border border-slate-200 overflow-hidden text-sm font-semibold">
          {[
            { id: 'all', label: 'All' },
            { id: 'configured', label: 'Configured' },
            { id: 'unconfigured', label: 'Not set up' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 transition-colors ${
                filter === tab.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 text-sm text-blue-900">
        <Info className="w-5 h-5 flex-shrink-0 text-blue-600 mt-0.5" />
        <div className="space-y-1 text-xs text-blue-800/90">
          <p>
            <strong>New product?</strong> Add it in{' '}
            <Link to="/products" className="text-blue-700 underline font-semibold">Product Catalog</Link>
            {' '}first — it will appear here automatically.
          </p>
          <p>
            Use <strong>varying quantity tiers</strong> for brackets (e.g. 0–5000 @ ₹0.20, 5001–10000 @ ₹0.25).
            Set <strong>min per order line</strong> when small orders should not count (RMC: 8 m³).
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Settings2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No products match your search.</p>
          <Link to="/products" className="text-emerald-600 text-sm font-semibold hover:underline mt-2 inline-block">
            Go to Product Catalog
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {configuredRows.length > 0 && filter !== 'unconfigured' && (
            <section className="space-y-3">
              <h2 className="text-sm font-bold text-slate-600 uppercase tracking-widest">
                Active incentive rules ({configuredRows.length})
              </h2>
              <div className="space-y-3">
                {configuredRows.map((row) => (
                  <div key={row.product_id} ref={(el) => { cardRefs.current[row.product_id] = el; }}>
                    <ProductIncentiveEditor
                      row={row}
                      draft={draft[row.product_id]}
                      canEdit={canEdit}
                      isExpanded={Boolean(expanded[row.product_id])}
                      onToggleExpand={() => toggleExpand(row.product_id)}
                      onUpdateDraft={(patch) => updateDraft(row.product_id, patch)}
                      onUpdateTier={(idx, field, val) => updateTier(row.product_id, idx, field, val)}
                      onAddTier={() => addTier(row.product_id)}
                      onRemoveTier={(idx) => removeTier(row.product_id, idx)}
                      onSetup={() => setupProduct(row.product_id)}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {unconfiguredRows.length > 0 && filter !== 'configured' && (
            <section className="space-y-3">
              <h2 className="text-sm font-bold text-slate-600 uppercase tracking-widest">
                Add incentive for other products ({unconfiguredRows.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {unconfiguredRows.map((row) => (
                  <div
                    key={row.product_id}
                    className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{row.product_name}</p>
                      {row.sub_item_label && (
                        <p className="text-xs text-slate-500 truncate">{row.sub_item_label}</p>
                      )}
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          setupProduct(row.product_id);
                          scrollToProduct(row.product_id);
                        }}
                        className="shrink-0 px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Set up tiers
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {unconfiguredRows.some((r) => expanded[r.product_id]) && (
                <div className="space-y-3 pt-2">
                  {unconfiguredRows
                    .filter((r) => expanded[r.product_id])
                    .map((row) => (
                      <div key={`edit-${row.product_id}`} ref={(el) => { cardRefs.current[row.product_id] = el; }}>
                        <ProductIncentiveEditor
                          row={row}
                          draft={draft[row.product_id]}
                          canEdit={canEdit}
                          isExpanded
                          onToggleExpand={() => toggleExpand(row.product_id)}
                          onUpdateDraft={(patch) => updateDraft(row.product_id, patch)}
                          onUpdateTier={(idx, field, val) => updateTier(row.product_id, idx, field, val)}
                          onAddTier={() => addTier(row.product_id)}
                          onRemoveTier={(idx) => removeTier(row.product_id, idx)}
                          onSetup={() => setupProduct(row.product_id)}
                        />
                      </div>
                    ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductIncentives;
